import { PartyCard } from "./PartyCard";

export interface ReportField {
  path: string;
  label: string;
  value: string;
  raw: unknown;
}

export interface ReportGroup {
  label: string;
  path: string;
  fields: ReportField[];
  groups: ReportGroup[];
}

export interface ReportIssue {
  location: string;
  path: string;
  rule: string;
  severity: string;
  message: string;
}

export interface Withdrawal {
  withdrawn_at: string;
  reason: string;
  reason_label: string;
  note: string | null;
  /** FINTRAC_DELETE = deleted by a Customer Admin from the portal (2026-10-09). */
  method?: "RECORDED" | "FINTRAC_DELETE";
  /** The deleting user's email; only for FINTRAC_DELETE. */
  by?: string | null;
}

export interface Outcome {
  state: "ACCEPTED" | "REJECTED" | "NOT_SUBMITTED" | "ERROR" | "CANCELLED" | "WITHDRAWN";
  http_status: number | null;
  message: string | null;
  reference: string | null;
  submitted_at: string | null;
  attempt: { n: number; of: number } | null;
  issues: ReportIssue[];
  /** Present only when state is WITHDRAWN (report_view._withdrawn_outcome). */
  withdrawal?: Withdrawal | null;
}

export interface ReportDocument {
  title: {
    client_name: string;
    report_type: string;
    report_type_label: string;
    candidate_id: string;
    generated_at: string;
  };
  outcome: Outcome;
  sections: { id: string; title: string; groups: ReportGroup[] }[];
}

const STATE_TEXT: Record<Outcome["state"], string> = {
  ACCEPTED: "Accepted by FINTRAC",
  REJECTED: "Rejected by FINTRAC",
  NOT_SUBMITTED: "Not yet submitted to FINTRAC",
  ERROR: "Submission error",
  // The API only ever produces this by promoting NOT_SUBMITTED (see
  // report_view.py's _cancelled_outcome()), so it always means "cancelled
  // before it was ever filed" -- the design spec's own wording (§8).
  CANCELLED: "Cancelled before filing",
  // Only ever produced from ACCEPTED: FINTRAC held the report, then deleted
  // it at the client's request (spec 2026-10-07).
  WITHDRAWN: "Withdrawn from FINTRAC",
};

export function OutcomeView({ outcome }: { outcome: Outcome }) {
  return (
    <div className="report-outcome">
      <p className={`pill ${outcome.state === "ACCEPTED" ? "ok"
        : outcome.state === "REJECTED" || outcome.state === "ERROR" ? "bad"
        : outcome.state === "WITHDRAWN" ? "withdrawn"
        : outcome.state === "CANCELLED" ? "mute" : "warn"}`}>
        {STATE_TEXT[outcome.state]}
      </p>
      <dl className="report-fields">
        {outcome.withdrawal && (
          <>
            <div className="report-field">
              <dt>{outcome.withdrawal.method === "FINTRAC_DELETE" ? "Deleted at FINTRAC" : "Withdrawn"}</dt>
              <dd>{outcome.withdrawal.withdrawn_at} · {outcome.withdrawal.reason_label}</dd>
            </div>
            {outcome.withdrawal.method === "FINTRAC_DELETE" && outcome.withdrawal.by && (
              <div className="report-field"><dt>Deleted by</dt><dd>{outcome.withdrawal.by}</dd></div>
            )}
            {outcome.withdrawal.note && (
              <div className="report-field"><dt>Note</dt><dd>{outcome.withdrawal.note}</dd></div>
            )}
          </>
        )}
        {outcome.reference && (
          <div className="report-field"><dt>FINTRAC reference</dt><dd>{outcome.reference}</dd></div>
        )}
        {outcome.submitted_at && (
          <div className="report-field"><dt>Submitted at</dt><dd>{outcome.submitted_at}</dd></div>
        )}
        {outcome.http_status != null && (
          <div className="report-field"><dt>HTTP status</dt><dd>{outcome.http_status}</dd></div>
        )}
        {outcome.message && (
          <div className="report-field"><dt>Message</dt><dd>{outcome.message}</dd></div>
        )}
        {outcome.attempt && (
          <div className="report-field">
            <dt>Attempt</dt><dd>{outcome.attempt.n} of {outcome.attempt.of}</dd>
          </div>
        )}
      </dl>
      {outcome.issues.length > 0 && (
        <>
          <h5>Issues reported by FINTRAC ({outcome.issues.length})</h5>
          <table className="report-issues">
            <thead>
              <tr><th>Where</th><th>Rule</th><th>Severity</th><th>What FINTRAC said</th></tr>
            </thead>
            <tbody>
              {outcome.issues.map((issue, index) => (
                <tr key={`${issue.path}-${index}`}>
                  <td>{issue.location || issue.path}</td>
                  <td>{issue.rule}</td>
                  <td>{issue.severity}</td>
                  <td>{issue.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

export function ReportView({ document }: { document: ReportDocument }) {
  return (
    <div className="report-view">
      {document.sections.map((section) => (
        <section key={section.id}>
          <h4>{section.title}</h4>
          {section.groups.map((group) => (
            <PartyCard key={group.path + group.label} group={group} />
          ))}
        </section>
      ))}
    </div>
  );
}
